/**
 * The portal's account operations, as an interface.
 *
 * This is the same shape of seam as {@link PortalApi}: components depend on
 * this type, never on `fetch`, so a test can hand a single component a stub and
 * assert what it was asked to do. The HTTP implementation lives in
 * `httpAuthApi.ts` and is the only thing that knows the endpoints exist.
 *
 * It is deliberately narrower than what `/v1/auth/*` offers. Every method here
 * is a step in a flow the portal actually has a page for; the day one needs
 * changing, the compiler names every caller.
 */

/** The account, as `GET /v1/me` projects it. */
export interface SessionUser {
  id: string;
  email: string;
  emailVerified: boolean;
  /** Derived from the address at sign-up — the form never asks for one. */
  name: string;
  createdAt: string;
}

/**
 * A failure the user is meant to read.
 *
 * `code` is the API's machine-readable reason, kept separate from `message` so
 * a page can branch on the cause — "verify your email first" needs a different
 * screen from "wrong password" — without matching on prose that changes.
 */
export class AuthError extends Error {
  readonly code: string | undefined;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

export interface AuthApi {
  /**
   * Create an account. Resolves when the verification email has been sent —
   * there is no session yet, by design, so the caller's next move is the
   * check-your-email page rather than the dashboard.
   */
  register(input: { email: string; password: string }): Promise<void>;

  signIn(input: { email: string; password: string }): Promise<void>;

  signOut(): Promise<void>;

  /** Always resolves, whether or not the address has an account: the API
   * refuses to disclose which, and the UI must not undo that by behaving
   * differently. */
  requestPasswordReset(email: string): Promise<void>;

  resetPassword(input: { token: string; password: string }): Promise<void>;

  /**
   * Change the password of the account already signed in, proving ownership
   * with the current one. Every *other* session is ended as a side effect —
   * see the note in `httpAuthApi.ts` for why that is not optional.
   */
  changePassword(input: { currentPassword: string; newPassword: string }): Promise<void>;

  /** Confirms an address from an emailed token, and signs the user in. */
  verifyEmail(token: string): Promise<void>;

  /** The signed-in account, or `null` when there is no session. Only a genuine
   * failure to ask — an unreachable API — rejects. */
  me(): Promise<SessionUser | null>;
}
